import React from "react";
import { Helmet } from "react-helmet";

const Terms = () => {
  return (
    <>
      <Helmet>
        <title>Terms | DojoTrack</title>
        <meta
          name="description"
          content="Read DojoTrack's terms and conditions. Learn about our service terms, subscription plans, data policies, and user responsibilities for our martial arts school management platform."
        />
        <meta
          name="keywords"
          content="dojo track terms, martial arts software terms, dojo management terms, dojo track conditions, martial arts software agreement"
        />
        <meta name="author" content="DojoTrack LLC" />
        <meta property="og:title" content="Terms | DojoTrack" />
        <meta
          property="og:description"
          content="Read DojoTrack's terms and conditions. Learn about our service terms, subscription plans, data policies, and user responsibilities."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dojotrack.com/terms" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Terms | DojoTrack" />
        <meta
          name="twitter:description"
          content="Read DojoTrack's terms and conditions. Learn about our service terms, subscription plans, data policies, and user responsibilities."
        />
        <link rel="canonical" href="https://dojotrack.com/terms" />
      </Helmet>
      <div className="bg-white dark:bg-gray-900">
        <div className="mx-auto max-w-4xl px-6 py-16 sm:py-24 lg:px-8">
          <div className="mx-auto max-w-2xl lg:mx-0">
            <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              Terms and Conditions
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-400">
              Last updated: December 1st, 2025
            </p>
          </div>
          <div className="mx-auto mt-16 max-w-2xl lg:mx-0 lg:max-w-none">
            <div className="prose prose-gray dark:prose-invert max-w-none [&>h2]:text-gray-900 dark:[&>h2]:text-white [&>h3]:text-gray-800 dark:[&>h3]:text-gray-200 [&>p]:text-gray-700 dark:[&>p]:text-gray-300 [&>ul]:text-gray-700 dark:[&>ul]:text-gray-300 [&>li]:text-gray-700 dark:[&>li]:text-gray-300 [&>strong]:text-gray-900 dark:[&>strong]:text-white [&_strong]:text-gray-900 dark:[&_strong]:text-white [&_em]:text-gray-700 dark:[&_em]:text-gray-300">
              <h2>1. Acceptance of Terms</h2>
              <p>
                By accessing and using DojoTrack ("Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>

              <h2>2. Description of Service</h2>
              <p>
                DojoTrack is a comprehensive martial arts studio management platform that provides:
              </p>
              <ul>
                <li>Student and lead management with detailed profiles</li>
                <li>Event creation and registration management</li>
                <li>Digital waiver creation and electronic signature collection</li>
                <li>Attendance tracking and class management</li>
                <li>Payment processing and billing management</li>
                <li>Email and SMS communication tools</li>
                <li>Analytics and reporting features</li>
                <li>Staff management and role assignments</li>
                <li>Photo and document upload capabilities</li>
              </ul>

              <h2>3. User Accounts and Responsibilities</h2>
              
              <h3>3.1 Account Creation</h3>
              <p>
                You must provide accurate, current, and complete information when creating an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
              </p>

              <h3>3.2 Studio Responsibilities</h3>
              <p>
                As a studio using our platform, you are responsible for:
              </p>
              <ul>
                <li>Obtaining proper consent for data collection from students and leads</li>
                <li>Ensuring compliance with applicable privacy laws and regulations</li>
                <li>Maintaining accurate and up-to-date student information</li>
                <li>Securing access to your DojoTrack account</li>
                <li>Managing staff permissions appropriately</li>
                <li>Ensuring event registrations and waivers are legally compliant</li>
              </ul>

              <h3>3.3 Data Accuracy and Consent</h3>
              <p>
                You must ensure that all student and lead information entered into the system is accurate and that you have obtained proper consent for data collection, including:
              </p>
              <ul>
                <li>Personal identification information</li>
                <li>Contact details and emergency information</li>
                <li>Medical information and allergies</li>
                <li>Payment and billing information</li>
                <li>Photos and profile images</li>
                <li>Event registration data</li>
                <li>Digital waiver submissions</li>
              </ul>

              <h2>4. Event Registration and Digital Waivers</h2>
              
              <h3>4.1 Event Registration</h3>
              <p>
                When creating events with registration capabilities, you agree to:
              </p>
              <ul>
                <li>Provide clear and accurate event information</li>
                <li>Obtain proper consent for data collection from registrants</li>
                <li>Comply with applicable laws regarding event registration</li>
                <li>Handle registration data securely and appropriately</li>
                <li>Provide clear privacy notices to registrants</li>
              </ul>

              <h3>4.2 Digital Waivers</h3>
              <p>
                When using our digital waiver features, you agree to:
              </p>
              <ul>
                <li>Create legally compliant waiver content</li>
                <li>Obtain proper electronic signatures and consent</li>
                <li>Maintain waiver records for required retention periods</li>
                <li>Ensure waiver submissions are legally binding</li>
                <li>Comply with electronic signature laws and regulations</li>
              </ul>

              <h2>5. Data and Privacy</h2>
              
              <h3>5.1 Data Collection and Use</h3>
              <p>
                Our platform collects and processes various types of personal information as described in our Privacy Policy. By using our service, you acknowledge and consent to this data collection and processing.
              </p>

              <h3>5.2 Data Security</h3>
              <p>
                We implement appropriate security measures to protect your data. However, you are also responsible for:
              </p>
              <ul>
                <li>Maintaining secure access to your account</li>
                <li>Not sharing account credentials with unauthorized users</li>
                <li>Reporting any security concerns immediately</li>
                <li>Ensuring staff members follow security protocols</li>
              </ul>

              <h3>5.3 Data Retention</h3>
              <p>
                We retain data according to our Privacy Policy and follow these specific practices:
              </p>
              <ul>
                <li>Your account and all associated data will be permanently deleted after six (6) months of inactivity (no logins and no indication of intent to recover the account). Deleted accounts cannot be restored.</li>
                <li>When you request account deletion, your data will be permanently deleted after a 30-day grace period. During this grace period, you can cancel your deletion request and reactivate your account.</li>
                <li>Attendance records are deleted three (3) months after their creation.</li>
                <li>Registration image data from events is deleted three (3) months after the event date.</li>
                <li>Logs of sent emails are deleted three (3) months after sending.</li>
                <li>Scheduled email logs and scheduled SMS message logs are deleted three (3) months after creation, regardless of whether they are sent.</li>
                <li>Appointment data is deleted three (3) months after the appointment date.</li>
                <li>Notification records are deleted three (3) months after their creation.</li>
              </ul>
              <p>
                In the event of business closure, bankruptcy, or cessation of DojoTrack operations, we will provide reasonable advance notice to allow you to export your data. We may transfer data retention obligations to a successor entity or handle data destruction in accordance with applicable legal requirements.
              </p>
              <p>
                You are responsible for:
              </p>
              <ul>
                <li>Understanding applicable data retention requirements</li>
                <li>Exporting important data before account closure</li>
                <li>Ensuring compliance with local data protection laws</li>
                <li>Maintaining your own backups of critical business data</li>
              </ul>

              <h3>5.4 Data Export Capabilities</h3>
              <p>
                To support your business continuity and data portability needs, we provide export functionality for:
              </p>
              <ul>
                <li>Event registration data in machine-readable formats</li>
                <li>Digital waiver submissions and associated records</li>
                <li>Student and lead management data</li>
                <li>Other key business data stored in your DojoTrack account</li>
              </ul>
              <p>
                We recommend regularly exporting critical data to ensure you have backups for your business records.
              </p>

              <h2>6. Payment and Subscription</h2>
              
              <h3>6.1 Subscription Terms</h3>
              <p>
                Subscription fees are billed in advance on a monthly or annual basis. You agree to pay all fees associated with your subscription plan.
              </p>

              <h3>6.2 Payment Processing</h3>
              <p>
                Payments are processed through Stripe. By using our payment features, you agree to Stripe's terms of service and privacy policy.
              </p>

              <h3>6.3 Cancellation</h3>
              <p>
                You may cancel your subscription at any time. Upon cancellation:
              </p>
              <ul>
                <li>Your account will remain active until the end of the current billing period</li>
                <li>You should export your data before the account is deactivated</li>
                <li>Your account will be subject to our standard inactivity deletion policy (6 months of no logins)</li>
                <li>Data with legal retention requirements (event registrations, digital waivers, payment records) will be retained for their required periods</li>
                <li>Operational data (attendance records, email logs, SMS logs) will be deleted according to our standard deletion schedule (3 months)</li>
                <li>We strongly recommend exporting all critical business data before or immediately after cancellation</li>
              </ul>

              <h3>6.4 Refund Policy</h3>
              <p>
                <strong>No Refunds:</strong> DojoTrack does not offer refunds for any reason whatsoever. All subscription fees, one-time payments, and service charges are non-refundable. This includes, but is not limited to:
              </p>
              <ul>
                <li>Monthly and annual subscription fees</li>
                <li>Plan upgrades or downgrades</li>
                <li>Service charges and processing fees</li>
                <li>One-time payments for additional services</li>
                <li>Partial refunds for unused portions of billing periods</li>
                <li>Refunds for service interruptions or technical issues</li>
                <li>Refunds for account cancellations or deletions</li>
              </ul>
              <p>
                By subscribing to DojoTrack, you acknowledge and agree that all payments are final and non-refundable. We recommend carefully evaluating our service before subscribing to ensure it meets your needs.
              </p>

              <h3>6.5 Payment Failure and Retry Policy</h3>
              <p>
                <strong>Payment Failure Notifications:</strong> DojoTrack will automatically send email notifications to account holders when payment methods fail or are approaching expiration. These notifications are sent to ensure uninterrupted service and provide timely updates regarding billing issues.
              </p>
              <p>
                <strong>Payment Method Updates:</strong> To resolve payment issues, account holders may either:
              </p>
              <ul>
                <li>Use the secure Stripe-hosted payment link provided in the notification email</li>
                <li>Log into their DojoTrack account, navigate to Settings → Billing, and select "Manage Billing" to update their payment method</li>
              </ul>
              <p>
                <strong>Automatic Retry Process:</strong> Our payment system will automatically attempt to process failed payments up to eight (8) times over a period of two (2) weeks. This retry schedule is designed to accommodate temporary payment issues while ensuring timely resolution.
              </p>
              <p>
                <strong>Subscription Cancellation:</strong> If all payment retry attempts fail within the two-week period, the subscription will be automatically cancelled. Upon cancellation, the account will remain accessible until the end of the current billing period.
              </p>
              <p>
                <strong>Account Reactivation:</strong> Following subscription cancellation due to payment failure, account holders may reactivate their subscription by logging into their DojoTrack account and updating their payment method through the billing management interface. Please note that accounts which remain inactive (no login activity) for six (6) months or longer are subject to permanent deletion from our database system as outlined in our data retention policy.
              </p>

              <h2>7. SMS Phone Number Rental and Messaging Services</h2>
              
              <h3>7.1 SMS Service Description</h3>
              <p>
                DojoTrack offers an optional SMS phone number rental service that allows studios to rent dedicated phone numbers for sending and receiving text messages with their students, leads, and contacts. This service is provided through our third-party SMS provider, Twilio.
              </p>

              <h3>7.2 Mandatory Compliance Requirements</h3>
              <p>
                <strong>By using our SMS phone number rental service, you agree to comply with all applicable telecommunications laws and regulations, including but not limited to:</strong>
              </p>
              <ul>
                <li><strong>TCPA (Telephone Consumer Protection Act)</strong> - You must obtain prior express written consent before sending marketing or promotional messages</li>
                <li><strong>CTIA Guidelines</strong> - You must follow all wireless industry best practices for SMS messaging</li>
                <li><strong>CAN-SPAM Act</strong> - You must provide clear opt-out mechanisms and honor opt-out requests</li>
                <li><strong>State and local telecommunications laws</strong> - You must comply with all applicable state and local regulations</li>
                <li><strong>International regulations</strong> - If messaging internationally, you must comply with applicable international laws</li>
              </ul>

              <h3>7.3 Consent and Opt-In Requirements</h3>
              <p>
                <strong>You are solely responsible for:</strong>
              </p>
              <ul>
                <li>Obtaining valid, documented consent from all recipients before sending them SMS messages</li>
                <li>Maintaining records of consent for all message recipients</li>
                <li>Ensuring consent is "prior express written consent" for marketing messages as required by TCPA</li>
                <li>Providing clear disclosure of message frequency and data rates when obtaining consent</li>
                <li>Never purchasing or renting contact lists for SMS messaging purposes</li>
                <li>Only messaging individuals who have explicitly opted in to receive messages from your studio</li>
              </ul>

              <h3>7.4 Opt-Out and STOP Request Management</h3>
              <p>
                <strong>You must immediately honor all opt-out requests, including:</strong>
              </p>
              <ul>
                <li>Responding to "STOP", "UNSUBSCRIBE", "CANCEL", "END", "QUIT" or similar opt-out keywords</li>
                <li>Ceasing all SMS communications to recipients who request opt-out</li>
                <li>Processing opt-out requests within a reasonable timeframe (typically within 24 hours or less)</li>
                <li>Maintaining an internal do-not-contact list to prevent future messages to opted-out recipients</li>
                <li>Not requiring recipients to pay message or data charges to opt out</li>
                <li>Honoring opt-out requests permanently unless the recipient explicitly opts back in</li>
              </ul>

              <h3>7.5 Prohibited SMS Activities</h3>
              <p>
                You agree NOT to use the SMS service for:
              </p>
              <ul>
                <li>Sending unsolicited commercial messages (spam)</li>
                <li>Sending messages to recipients who have not provided valid consent</li>
                <li>Sending messages after a recipient has opted out</li>
                <li>Sharing, selling, or renting phone numbers to third parties</li>
                <li>Sending fraudulent, deceptive, or misleading messages</li>
                <li>Sending messages that violate any laws or regulations</li>
                <li>Using the service for phishing, scams, or other illegal activities</li>
                <li>Sending excessive message volume that could be considered harassment</li>
                <li>Spoofing caller ID or sender information</li>
                <li>Facilitating or encouraging any illegal activities</li>
              </ul>

              <h3>7.6 Message Content Requirements</h3>
              <p>
                All SMS messages sent through our service must:
              </p>
              <ul>
                <li>Clearly identify your studio as the sender</li>
                <li>Provide accurate and truthful information</li>
                <li>Include opt-out instructions when required by law</li>
                <li>Comply with content restrictions imposed by carriers and regulators</li>
                <li>Not contain malicious links, malware, or harmful content</li>
              </ul>

              <h3>7.7 Your Liability and Indemnification</h3>
              <p>
                <strong>You acknowledge and agree that:</strong>
              </p>
              <ul>
                <li><strong>You are solely responsible</strong> for all messages sent using your rented phone number</li>
                <li><strong>You bear all liability</strong> for violations of telecommunications laws, including TCPA violations which can result in penalties of $500-$1,500 per violation</li>
                <li><strong>You will indemnify, defend, and hold harmless DojoTrack LLC</strong> from any claims, damages, fines, penalties, legal fees, or expenses arising from your use of the SMS service, including but not limited to:
                  <ul>
                    <li>TCPA violations and class action lawsuits</li>
                    <li>Privacy law violations</li>
                    <li>Failure to honor opt-out requests</li>
                    <li>Sending messages without proper consent</li>
                    <li>Any other violations of applicable laws or regulations</li>
                  </ul>
                </li>
                <li>DojoTrack LLC is not responsible for your compliance with telecommunications laws</li>
                <li>DojoTrack LLC does not review or approve your message content or recipient lists</li>
              </ul>

              <h3>7.8 Service Suspension and Termination</h3>
              <p>
                DojoTrack reserves the right to:
              </p>
              <ul>
                <li>Immediately suspend or terminate your SMS service if we receive complaints about your messaging practices</li>
                <li>Investigate suspected violations of these terms or applicable laws</li>
                <li>Report violations to relevant regulatory authorities</li>
                <li>Cooperate with law enforcement and regulatory investigations</li>
                <li>Release your phone number and terminate service for repeated violations</li>
                <li>Take any action required by Twilio or wireless carriers</li>
              </ul>

              <h3>7.9 No Guarantee of Delivery</h3>
              <p>
                SMS message delivery is subject to carrier networks and recipient device availability. DojoTrack does not guarantee message delivery and is not liable for:
              </p>
              <ul>
                <li>Failed or delayed message delivery</li>
                <li>Carrier filtering or blocking of messages</li>
                <li>Service interruptions or outages</li>
                <li>Changes in carrier policies or regulations</li>
              </ul>

              <h3>7.10 SMS Billing and Fees</h3>
              <p>
                SMS phone number rental and usage fees are separate from your DojoTrack subscription and are billed according to the pricing displayed in the application. Fees may include:
              </p>
              <ul>
                <li>Monthly phone number rental fees</li>
                <li>Per-message sending and receiving charges</li>
                <li>Additional fees imposed by carriers or service providers</li>
              </ul>
              <p>
                All SMS service fees are non-refundable and subject to the refund policy stated in Section 6.4 of these Terms.
              </p>

              <h3>7.11 Data Protection for Mobile Phone Numbers</h3>
              <p>
                You agree that:
              </p>
              <ul>
                <li>You will not share, sell, or rent mobile phone numbers collected through DojoTrack to third parties for marketing purposes</li>
                <li>You will maintain appropriate security measures to protect phone number data</li>
                <li>You will comply with all applicable privacy laws regarding mobile phone number collection and use</li>
                <li>You will include mobile phone number protection provisions in your own privacy policy</li>
              </ul>

              <h3>7.12 Acknowledgment of Risk</h3>
              <p>
                <strong>You acknowledge that violations of TCPA and other telecommunications laws can result in significant financial penalties, including:</strong>
              </p>
              <ul>
                <li>Statutory damages of $500 per violation, or $1,500 per willful violation</li>
                <li>Class action lawsuits with potentially millions of dollars in liability</li>
                <li>Regulatory investigations and enforcement actions</li>
                <li>Reputational damage to your business</li>
              </ul>
              <p>
                <strong>By using our SMS service, you acknowledge these risks and agree to assume full responsibility for compliance.</strong>
              </p>

              <h2>8. Acceptable Use</h2>
              <p>
                You agree not to use the Service to:
              </p>
              <ul>
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe on the rights of others</li>
                <li>Upload malicious content or attempt to compromise system security</li>
                <li>Use the service for unauthorized commercial purposes</li>
                <li>Collect or store personal data without proper consent</li>
                <li>Create false or misleading event registrations</li>
                <li>Use the platform for illegal activities</li>
              </ul>

              <h2>9. Intellectual Property</h2>
              <p>
                The Service and its original content, features, and functionality are owned by DojoTrack LLC and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
              </p>

              <h2>10. Third-Party Services</h2>
              <p>
                Our platform integrates with third-party services including:
              </p>
              <ul>
                <li>Stripe (payment processing)</li>
                <li>Gmail (email communications)</li>
                <li>Twilio (SMS messaging)</li>
                <li>OpenAI (AI features)</li>
                <li>Supabase (database and authentication)</li>
              </ul>
              <p>
                Use of these services is subject to their respective terms of service and privacy policies. When using our SMS phone number rental service, you also agree to comply with Twilio's Acceptable Use Policy and Messaging Policy.
              </p>

              <h2>11. Limitation of Liability</h2>
              <p>
                DojoTrack LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the Service. This limitation of liability applies with particular emphasis to SMS messaging services, where DojoTrack LLC is not liable for any TCPA violations, regulatory fines, penalties, or legal claims arising from your messaging practices.
              </p>

              <h2>12. Disclaimer of Warranties</h2>
              <p>
                The Service is provided on an "AS IS" and "AS AVAILABLE" basis. DojoTrack LLC makes no warranties, expressed or implied, and hereby disclaims all warranties, including without limitation, warranties of merchantability, fitness for a particular purpose, and non-infringement. DojoTrack does not warrant that SMS messages will be delivered, that the service will be uninterrupted, or that it will meet your specific requirements.
              </p>

              <h2>13. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless DojoTrack LLC, its officers, directors, employees, agents, and affiliates from and against any and all claims, damages, obligations, losses, liabilities, costs, debts, and expenses (including but not limited to attorney's fees) arising from:
              </p>
              <ul>
                <li>Your use of the Service or violation of these Terms</li>
                <li>Your violation of any third party right, including without limitation any copyright, property, or privacy right</li>
                <li>Your violation of any applicable law or regulation</li>
                <li><strong>Your use of SMS messaging services, including but not limited to TCPA violations, failure to obtain proper consent, failure to honor opt-out requests, or any other telecommunications law violations</strong></li>
                <li>Any claim that your use of the Service caused damage to a third party</li>
              </ul>
              <p>
                This indemnification obligation will survive termination of these Terms and your use of the Service.
              </p>

              <h2>14. Termination</h2>
              <p>
                We may terminate or suspend your account immediately, without prior notice, for any reason, including breach of these Terms. Upon termination, your right to use the Service will cease immediately. Termination may result from, but is not limited to, violations of SMS messaging compliance requirements, repeated complaints about your messaging practices, or suspected illegal activities.
              </p>

              <h2>15. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which DojoTrack LLC operates, without regard to its conflict of law provisions.
              </p>

              <h2>16. Changes to Terms</h2>
              <p>
                We reserve the right to modify these terms at any time. We will notify users of any material changes by posting the new terms on our website. Your continued use of the Service after such changes constitutes acceptance of the new terms.
              </p>

              <h2>17. Contact Information</h2>
              <p>
                If you have any questions about these Terms and Conditions, please contact us at:
              </p>
              <p>
                support@dojotrack.com
              </p>

              <h2>18. Communications and Marketing</h2>
              <p>
                By creating an account or using DojoTrack, you agree that we may send you emails and other communications for reasons including, but not limited to: service notifications, platform updates, onboarding information, customer support, newsletters, promotions, marketing, and product announcements.
              </p>
              <p>
                You may opt out of receiving marketing or promotional communications at any time by following the unsubscribe link provided in those emails or by contacting us at support@dojotrack.com. However, you will still receive essential service-related or account notifications.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Terms;
